import { useState } from "react";
import { motion } from "motion/react";
import Toast from "./Toast";

interface TestReportFormData {
  orderId: string;
  projectName: string;
  clientName: string;
  docRefNo: string;
  date: string;
  cementType: string;
  cubeSize: string;
  gradeOfConcrete: string;
  tests: TestEntry[];
  vehicleDetails?: {
    vehicleNumber: string;
    driverName: string;
    departureTime: string;
  };
  remarks: string;
  signatures: {
    engineerQAQC: string;
    managerQAQC: string;
    projectManager: string;
  };
}

interface TestEntry {
  srNo: number;
  location: string;
  slumpMm: string;
  cubeIdMark: string;
  dateOfCasting: string;
  dateOfTesting: string;
  timeOfTesting: string;
  ageOfCube: string;
  weightOfCube: string;
  crushingLoad: string;
  compressiveStrength: string;
  avgCompressiveStrength?: string;
}

interface TestReportFormProps {
  orderId?: string;
  onSubmit: (data: TestReportFormData) => void;
  onCancel: () => void;
}

export default function TestReportForm({
  orderId = "",
  onSubmit,
  onCancel,
}: TestReportFormProps) {
  const [formData, setFormData] = useState<TestReportFormData>({
    orderId: orderId,
    projectName: "",
    clientName: "",
    docRefNo: `DOC-${Date.now()}`,
    date: new Date().toISOString().split("T")[0],
    cementType: "OPC 53 Grade",
    cubeSize: "150mm×150mm×150mm",
    gradeOfConcrete: "M30",
    tests: [
      {
        srNo: 1,
        location: "",
        slumpMm: "",
        cubeIdMark: "",
        dateOfCasting: "",
        dateOfTesting: "",
        timeOfTesting: "",
        ageOfCube: "",
        weightOfCube: "",
        crushingLoad: "",
        compressiveStrength: "",
      },
    ],
    vehicleDetails: {
      vehicleNumber: "",
      driverName: "",
      departureTime: "",
    },
    remarks: "",
    signatures: {
      engineerQAQC: "",
      managerQAQC: "",
      projectManager: "",
    },
  });

  const [showToast, setShowToast] = useState<{
    message: string;
    type: "success" | "info" | "error";
  } | null>(null);

  const handleInputChange = (
    field: keyof TestReportFormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTestChange = (
    index: number,
    field: keyof TestEntry,
    value: string
  ) => {
    const updatedTests = [...formData.tests];
    updatedTests[index] = { ...updatedTests[index], [field]: value };

    // Calculate compressive strength automatically
    if (field === "crushingLoad" || field === "weightOfCube") {
      const load = parseFloat(updatedTests[index].crushingLoad || "0");
      const area = 22500; // 150mm × 150mm = 22500 mm²
      if (load > 0) {
        const strength = ((load * 1000) / area).toFixed(2);
        updatedTests[index].compressiveStrength = strength;
      }
    }

    // Calculate age of cube automatically
    if (field === "dateOfCasting" || field === "dateOfTesting") {
      const casting = new Date(updatedTests[index].dateOfCasting);
      const testing = new Date(updatedTests[index].dateOfTesting);
      if (casting && testing && testing >= casting) {
        const diffTime = Math.abs(testing.getTime() - casting.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        updatedTests[index].ageOfCube = `${diffDays} days`;
      }
    }

    setFormData((prev) => ({ ...prev, tests: updatedTests }));
  };

  const addTestEntry = () => {
    setFormData((prev) => ({
      ...prev,
      tests: [
        ...prev.tests,
        {
          srNo: prev.tests.length + 1,
          location: "",
          slumpMm: "",
          cubeIdMark: "",
          dateOfCasting: "",
          dateOfTesting: "",
          timeOfTesting: "",
          ageOfCube: "",
          weightOfCube: "",
          crushingLoad: "",
          compressiveStrength: "",
        },
      ],
    }));
  };

  const removeTestEntry = (index: number) => {
    if (formData.tests.length > 1) {
      const updatedTests = formData.tests.filter((_, i) => i !== index);
      // Renumber
      updatedTests.forEach((test, idx) => {
        test.srNo = idx + 1;
      });
      setFormData((prev) => ({ ...prev, tests: updatedTests }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Calculate average compressive strength
    const strengths = formData.tests
      .map((t) => parseFloat(t.compressiveStrength || "0"))
      .filter((s) => s > 0);
    const avgStrength =
      strengths.length > 0
        ? (strengths.reduce((a, b) => a + b, 0) / strengths.length).toFixed(2)
        : "0";

    // Set average for all tests
    const updatedTests = formData.tests.map((test) => ({
      ...test,
      avgCompressiveStrength: avgStrength,
    }));

    const finalData = { ...formData, tests: updatedTests };

    onSubmit(finalData);
    setShowToast({
      message: "Test report created successfully",
      type: "success",
    });
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1B2A5B] to-[#0A0F1C] text-white px-5 md:px-8 lg:px-12 pt-8 pb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <h1
              style={{
                fontSize: "clamp(24px, 5vw, 32px)",
                letterSpacing: "-0.03em",
                fontWeight: "800",
              }}
            >
              Test Report Form
            </h1>
            <button
              onClick={onCancel}
              className="w-10 h-10 rounded-full backdrop-blur-xl flex items-center justify-center"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 3L17 17M3 17L17 3"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
          <p
            className="text-white/70"
            style={{
              fontSize: "14px",
              letterSpacing: "0.01em",
              fontWeight: "500",
            }}
          >
            Concrete Cube Test Report
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="px-5 md:px-8 lg:px-12 pt-6 space-y-6 max-w-7xl mx-auto">
        {/* Project Details Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-3xl p-6"
          style={{
            boxShadow:
              "0 2px 8px rgba(0, 0, 0, 0.04), 0 12px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          <h2
            className="text-[#0A0F1C] mb-5"
            style={{ fontSize: "20px", fontWeight: "800" }}
          >
            Project Information
          </h2>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label
                className="text-[#717182] mb-2 block"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                Order ID
              </label>
              <input
                type="text"
                value={formData.orderId}
                onChange={(e) => handleInputChange("orderId", e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                style={{ fontSize: "14px" }}
                placeholder="RMC-2024-12-2847"
                required
              />
            </div>

            <div>
              <label
                className="text-[#717182] mb-2 block"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                Name of Project
              </label>
              <input
                type="text"
                value={formData.projectName}
                onChange={(e) =>
                  handleInputChange("projectName", e.target.value)
                }
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                style={{ fontSize: "14px" }}
                placeholder="Enter project name"
                required
              />
            </div>

            <div>
              <label
                className="text-[#717182] mb-2 block"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                Name of Client
              </label>
              <input
                type="text"
                value={formData.clientName}
                onChange={(e) =>
                  handleInputChange("clientName", e.target.value)
                }
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                style={{ fontSize: "14px" }}
                placeholder="Enter client name"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className="text-[#717182] mb-2 block"
                  style={{ fontSize: "12px", fontWeight: "600" }}
                >
                  Doc Ref No
                </label>
                <input
                  type="text"
                  value={formData.docRefNo}
                  onChange={(e) =>
                    handleInputChange("docRefNo", e.target.value)
                  }
                  className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                  style={{ fontSize: "14px" }}
                  required
                />
              </div>

              <div>
                <label
                  className="text-[#717182] mb-2 block"
                  style={{ fontSize: "12px", fontWeight: "600" }}
                >
                  Date
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange("date", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                  style={{ fontSize: "14px" }}
                  required
                />
              </div>
            </div>

            <div>
              <label
                className="text-[#717182] mb-2 block"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                Cement Type
              </label>
              <select
                value={formData.cementType}
                onChange={(e) =>
                  handleInputChange("cementType", e.target.value)
                }
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                style={{ fontSize: "14px" }}
              >
                <option>OPC 53 Grade</option>
                <option>OPC 43 Grade</option>
                <option>PPC</option>
                <option>PSC</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className="text-[#717182] mb-2 block"
                  style={{ fontSize: "12px", fontWeight: "600" }}
                >
                  Cube Size
                </label>
                <input
                  type="text"
                  value={formData.cubeSize}
                  onChange={(e) =>
                    handleInputChange("cubeSize", e.target.value)
                  }
                  className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                  style={{ fontSize: "14px" }}
                  required
                />
              </div>

              <div>
                <label
                  className="text-[#717182] mb-2 block"
                  style={{ fontSize: "12px", fontWeight: "600" }}
                >
                  Grade of Concrete
                </label>
                <select
                  value={formData.gradeOfConcrete}
                  onChange={(e) =>
                    handleInputChange("gradeOfConcrete", e.target.value)
                  }
                  className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                  style={{ fontSize: "14px" }}
                >
                  <option>M10</option>
                  <option>M15</option>
                  <option>M20</option>
                  <option>M25</option>
                  <option>M30</option>
                  <option>M35</option>
                  <option>M40</option>
                  <option>M45</option>
                  <option>M50</option>
                </select>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Vehicle Details Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-3xl p-6"
          style={{
            boxShadow:
              "0 2px 8px rgba(0, 0, 0, 0.04), 0 12px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          <h2
            className="text-[#0A0F1C] mb-5"
            style={{ fontSize: "20px", fontWeight: "800" }}
          >
            Vehicle Details
          </h2>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label
                className="text-[#717182] mb-2 block"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                Vehicle Number
              </label>
              <input
                type="text"
                value={formData.vehicleDetails?.vehicleNumber || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    vehicleDetails: {
                      ...prev.vehicleDetails!,
                      vehicleNumber: e.target.value,
                    },
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                style={{ fontSize: "14px" }}
                placeholder="TN-01-AB-1234"
              />
            </div>

            <div>
              <label
                className="text-[#717182] mb-2 block"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                Driver Name
              </label>
              <input
                type="text"
                value={formData.vehicleDetails?.driverName || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    vehicleDetails: {
                      ...prev.vehicleDetails!,
                      driverName: e.target.value,
                    },
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                style={{ fontSize: "14px" }}
                placeholder="Enter driver name"
              />
            </div>

            <div>
              <label
                className="text-[#717182] mb-2 block"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                Departure Time
              </label>
              <input
                type="time"
                value={formData.vehicleDetails?.departureTime || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    vehicleDetails: {
                      ...prev.vehicleDetails!,
                      departureTime: e.target.value,
                    },
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                style={{ fontSize: "14px" }}
              />
            </div>
          </div>
        </motion.div>

        {/* Test Entries Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-3xl p-6"
          style={{
            boxShadow:
              "0 2px 8px rgba(0, 0, 0, 0.04), 0 12px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          <div className="flex items-center justify-between mb-5">
            <h2
              className="text-[#0A0F1C]"
              style={{ fontSize: "20px", fontWeight: "800" }}
            >
              Test Entries
            </h2>
            <motion.button
              type="button"
              whileTap={{ scale: 0.95 }}
              onClick={addTestEntry}
              className="px-4 py-2 rounded-xl text-white"
              style={{
                background:
                  "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                fontSize: "13px",
                fontWeight: "700",
              }}
            >
              + Add Test
            </motion.button>
          </div>

          {formData.tests.map((test, index) => (
            <div
              key={index}
              className="mb-6 pb-6 border-b border-[#E5E7EB] last:border-0"
            >
              <div className="flex items-center justify-between mb-4">
                <div
                  className="text-[#1B2A5B]"
                  style={{ fontSize: "16px", fontWeight: "700" }}
                >
                  Test #{test.srNo}
                </div>
                {formData.tests.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTestEntry(index)}
                    className="text-red-500 hover:text-red-700"
                    style={{ fontSize: "12px", fontWeight: "600" }}
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label
                    className="text-[#717182] mb-2 block"
                    style={{ fontSize: "12px", fontWeight: "600" }}
                  >
                    Location
                  </label>
                  <input
                    type="text"
                    value={test.location}
                    onChange={(e) =>
                      handleTestChange(index, "location", e.target.value)
                    }
                    className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                    style={{ fontSize: "14px" }}
                    placeholder="e.g., Slab-1, Column-A"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      className="text-[#717182] mb-2 block"
                      style={{ fontSize: "12px", fontWeight: "600" }}
                    >
                      Slump (mm)
                    </label>
                    <input
                      type="number"
                      value={test.slumpMm}
                      onChange={(e) =>
                        handleTestChange(index, "slumpMm", e.target.value)
                      }
                      className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                      style={{ fontSize: "14px" }}
                      placeholder="85"
                      required
                    />
                  </div>

                  <div>
                    <label
                      className="text-[#717182] mb-2 block"
                      style={{ fontSize: "12px", fontWeight: "600" }}
                    >
                      Cube ID Mark
                    </label>
                    <input
                      type="text"
                      value={test.cubeIdMark}
                      onChange={(e) =>
                        handleTestChange(index, "cubeIdMark", e.target.value)
                      }
                      className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                      style={{ fontSize: "14px" }}
                      placeholder="C-001"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      className="text-[#717182] mb-2 block"
                      style={{ fontSize: "12px", fontWeight: "600" }}
                    >
                      Date of Casting
                    </label>
                    <input
                      type="date"
                      value={test.dateOfCasting}
                      onChange={(e) =>
                        handleTestChange(index, "dateOfCasting", e.target.value)
                      }
                      className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                      style={{ fontSize: "14px" }}
                      required
                    />
                  </div>

                  <div>
                    <label
                      className="text-[#717182] mb-2 block"
                      style={{ fontSize: "12px", fontWeight: "600" }}
                    >
                      Date of Testing
                    </label>
                    <input
                      type="date"
                      value={test.dateOfTesting}
                      onChange={(e) =>
                        handleTestChange(index, "dateOfTesting", e.target.value)
                      }
                      className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                      style={{ fontSize: "14px" }}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      className="text-[#717182] mb-2 block"
                      style={{ fontSize: "12px", fontWeight: "600" }}
                    >
                      Time of Testing
                    </label>
                    <input
                      type="time"
                      value={test.timeOfTesting}
                      onChange={(e) =>
                        handleTestChange(index, "timeOfTesting", e.target.value)
                      }
                      className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                      style={{ fontSize: "14px" }}
                      required
                    />
                  </div>

                  <div>
                    <label
                      className="text-[#717182] mb-2 block"
                      style={{ fontSize: "12px", fontWeight: "600" }}
                    >
                      Age of Cube
                    </label>
                    <input
                      type="text"
                      value={test.ageOfCube}
                      className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB]"
                      style={{ fontSize: "14px" }}
                      placeholder="Auto-calculated"
                      readOnly
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      className="text-[#717182] mb-2 block"
                      style={{ fontSize: "12px", fontWeight: "600" }}
                    >
                      Weight of Cube (Grams)
                    </label>
                    <input
                      type="number"
                      value={test.weightOfCube}
                      onChange={(e) =>
                        handleTestChange(index, "weightOfCube", e.target.value)
                      }
                      className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                      style={{ fontSize: "14px" }}
                      placeholder="8250"
                      required
                    />
                  </div>

                  <div>
                    <label
                      className="text-[#717182] mb-2 block"
                      style={{ fontSize: "12px", fontWeight: "600" }}
                    >
                      Crushing Load (KN)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={test.crushingLoad}
                      onChange={(e) =>
                        handleTestChange(index, "crushingLoad", e.target.value)
                      }
                      className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                      style={{ fontSize: "14px" }}
                      placeholder="760.5"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label
                    className="text-[#717182] mb-2 block"
                    style={{ fontSize: "12px", fontWeight: "600" }}
                  >
                    Compressive Strength (N/mm²)
                  </label>
                  <input
                    type="text"
                    value={test.compressiveStrength}
                    className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB]"
                    style={{ fontSize: "14px", fontWeight: "700" }}
                    placeholder="Auto-calculated"
                    readOnly
                  />
                </div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Remarks & Signatures */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white rounded-3xl p-6"
          style={{
            boxShadow:
              "0 2px 8px rgba(0, 0, 0, 0.04), 0 12px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          <h2
            className="text-[#0A0F1C] mb-5"
            style={{ fontSize: "20px", fontWeight: "800" }}
          >
            Remarks & Signatures
          </h2>

          <div className="space-y-4">
            <div>
              <label
                className="text-[#717182] mb-2 block"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                Remarks
              </label>
              <textarea
                value={formData.remarks}
                onChange={(e) => handleInputChange("remarks", e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                style={{ fontSize: "14px" }}
                rows={4}
                placeholder="Enter any additional remarks or observations"
              />
            </div>

            <div>
              <label
                className="text-[#717182] mb-2 block"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                Engineer QA/QC Signature
              </label>
              <input
                type="text"
                value={formData.signatures.engineerQAQC}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    signatures: {
                      ...prev.signatures,
                      engineerQAQC: e.target.value,
                    },
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                style={{ fontSize: "14px" }}
                placeholder="Enter engineer name"
                required
              />
            </div>

            <div>
              <label
                className="text-[#717182] mb-2 block"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                Manager QA/QC Signature
              </label>
              <input
                type="text"
                value={formData.signatures.managerQAQC}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    signatures: {
                      ...prev.signatures,
                      managerQAQC: e.target.value,
                    },
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                style={{ fontSize: "14px" }}
                placeholder="Enter manager name"
                required
              />
            </div>

            <div>
              <label
                className="text-[#717182] mb-2 block"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                Project Manager Signature
              </label>
              <input
                type="text"
                value={formData.signatures.projectManager}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    signatures: {
                      ...prev.signatures,
                      projectManager: e.target.value,
                    },
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:outline-none focus:border-[#1B2A5B]"
                style={{ fontSize: "14px" }}
                placeholder="Enter project manager name"
                required
              />
            </div>
          </div>
        </motion.div>

        {/* Submit Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <motion.button
            type="button"
            whileTap={{ scale: 0.98 }}
            onClick={onCancel}
            className="py-4 rounded-2xl"
            style={{
              backgroundColor: "white",
              color: "#717182",
              fontSize: "16px",
              fontWeight: "700",
              border: "2px solid #E5E7EB",
            }}
          >
            Cancel
          </motion.button>

          <motion.button
            type="submit"
            whileTap={{ scale: 0.98 }}
            className="py-4 rounded-2xl text-white"
            style={{
              background:
                "linear-gradient(135deg, #1B2A5B 0%, #2A3F7A 100%)",
              fontSize: "16px",
              fontWeight: "700",
              boxShadow: "0 8px 24px rgba(27, 42, 91, 0.3)",
            }}
          >
            Create Report
          </motion.button>
        </div>
      </form>

      {/* Toast */}
      {showToast && (
        <Toast
          message={showToast.message}
          type={showToast.type}
          onClose={() => setShowToast(null)}
        />
      )}
    </div>
  );
}